import { defineNuxtRouteMiddleware, useRuntimeConfig } from '#imports'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { merge } from '@backmarket/utils/object/merge'

import { useGuestMagicLink } from '../composables/useGuestMagicLink'
import { useRedirectLocale } from '../composables/useRedirectLocale'
import { useUserStore } from '../composables/useUserStore'
import type { AuthLevel } from '../types'
import { findRouteOrParentMatchingPredicate } from '../utils/findRouteOrParentMatchingPredicate'

function getAuthLevel(isAuthenticated: boolean, isGuest: boolean): AuthLevel {
  if (isAuthenticated) return 'authenticated'
  if (isGuest) return 'guest'
  return 'unauthenticated'
}

export default defineNuxtRouteMiddleware(async (route) => {
  const config = useRuntimeConfig().public.oauth
  const { isUserFetched, isUserAuthenticated, isUserGuest, fetchUser } =
    useUserStore()
  const { handleGuestMagicLink } = useGuestMagicLink()
  const { getRouteLocationWithLocale } = useRedirectLocale()

  const matchedRoute = findRouteOrParentMatchingPredicate(
    route,
    (routeDefinition) =>
      routeDefinition.meta !== undefined &&
      routeDefinition.meta.auth !== undefined,
  )

  // No auth meta in the route hierarchy = public route
  if (matchedRoute === undefined) return undefined

  const routeAuth = matchedRoute.meta.auth
  if (routeAuth === undefined) return undefined

  // Guest magic-link flow — token exchange, session establishment, etc.
  if (routeAuth.mode === 'guest-magic-link') {
    return handleGuestMagicLink(route, routeAuth.redirection)
  }

  // No restriction, anyone passes (overrides a restricted parent)
  if (routeAuth.mode === 'public') return undefined

  if (!isUserFetched.value) {
    await fetchUser()
  }

  const currentLevel = getAuthLevel(
    isUserAuthenticated.value,
    isUserGuest.value,
  )

  if (routeAuth.mode.includes(currentLevel)) return undefined

  // Temporary: let guests through "authenticated" mode when the experiment is enabled
  if (
    useExperiments()['experiment.guestAsAuthenticated'] === 'enabled' &&
    currentLevel === 'guest' &&
    routeAuth.mode.includes('authenticated')
  ) {
    return undefined
  }

  const redirectOption =
    routeAuth.redirection?.[currentLevel] ??
    routeAuth.redirection?.default ??
    getRouteLocationWithLocale(config.defaultRedirectLocations[currentLevel])

  if ('to' in redirectOption) {
    if (redirectOption.skipNext) return { ...redirectOption.to }

    return merge({}, redirectOption.to, { query: { next: route.fullPath } })
  }

  return merge({}, redirectOption, { query: { next: route.fullPath } })
})
